'use client';

import Link from '@eliadmin/core/src/components/link';
import { ROUTES } from '@eliadmin/core/src/constants/routes';

import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../shared/heading';
import { Button } from '../ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@eliadmin/core/src/components/ui/accordion';
import { HtmlRenderer } from '@eliadmin/core/src/components/ui/html-renderer';
import { LuMinus, LuPlus } from 'react-icons/lu';
import React from 'react';

interface FAQProps {
  faq: TDynamicData[];
  title: string;
  bannerTitle: string;
}

export function FAQ({ faq, title, bannerTitle }: FAQProps) {
  const [isExpanded, setIsExpanded] = React.useState<string | null>(null);

  return (
    <section className={cn('pb-12 pt-16 sm:pb-16 sm:pt-20 lg:pt-24', 'custom-section')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-12 md:flex-row',
        )}
      >
        <div className="flex w-full flex-col items-start justify-between gap-8 md:max-w-[505px]">
          <Heading as="h2" title={title} className="w-full max-w-[250px]" />
          <div className="flex w-full flex-col items-start justify-start gap-6 rounded-[8px] bg-[#F5F5F5] p-6 sm:gap-8 sm:p-8">
            <h3 data-ea-visual-tag="h4" className="text-xl font-normal text-foreground sm:text-2xl">{bannerTitle}</h3>
            <Button asChild>
              <Link prefetch={false} href={ROUTES.CONTACT()}>
                Kontaktovať
              </Link>
            </Button>
          </div>
        </div>
        <Accordion
          onValueChange={value => setIsExpanded(value)}
          type="single"
          collapsible
          className="w-full md:max-w-[610px]"
        >
          {faq.map((item, i) => (
            <AccordionItem
              value={item.name}
              key={item.name}
              className={cn(
                'w-full border-b border-t-0 border-b-[#E9E9E9] px-0 py-4',
                i === faq.length - 1 && 'border-b-0',
              )}
            >
              <AccordionTrigger
                hideIcon
                className={cn(
                  '!hover:no-underline flex w-full items-center justify-between p-0 text-left text-base text-foreground sm:text-lg lg:text-xl',
                  'ea-questions__trigger',
                )}
              >
                {item.name}
                {isExpanded === item.name ? (
                  <LuMinus className="h-6 w-6 text-primary" />
                ) : (
                  <LuPlus className="h-6 w-6 text-primary" />
                )}
              </AccordionTrigger>
              <AccordionContent className="pt-4">
                <HtmlRenderer
                  html={item.fields[0].value}
                  className="w-full text-left text-[#838383]"
                />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
